/* GuideSections.jsx — renderers for the Career Clarity guidance JSON (v2.1.0).
 * Generic object/list renderer + small special cases (routes, automation
 * stages, field examples, regional selector, qualification check, links).
 * Text is rendered verbatim from the JSON; labels for scenario/eligibility
 * fields are shown as chips so facts and predictions stay distinguishable. */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, MapPin, ShieldAlert } from 'lucide-react';

/* Learnixta destinations supplied in the JSON → existing internal routes */
export const INTERNAL = {
  'https://learnixta.com/blue-collar': '/blue-collar',
  'https://learnixta.com/ownpath': '/ownpath',
};

const HIDE = new Set(['id', 'source_ids', 'heading', 'title', 'label', 'route_ids', 'regional_quiz_ids', 'linked_pathway_id', 'step', 'stage_id']);
const CHIP_KEYS = new Set(['label', 'status', 'evidence_status', 'link_status', 'retrieval_status', 'admission_status']);
const LABELS = {
  what_you_study: 'What you study', small_trial: 'Small trial', next_step_note: 'Next step', core_subjects: 'Core subjects',
  entry_check: 'Entry check', how_to_proceed: 'How to proceed', cost_check: 'Cost check', choose_after_checking: 'Choose after checking',
  automation_reality: 'Automation reality', jurisdiction_note: 'Jurisdiction note', admission_status: 'Admission status',
  formal_trade: 'Formal trade', course_duration: 'Course duration', work_picture: 'Work picture', first_trial: 'First trial',
  risk_picture: 'Risk picture', automation_note: 'Automation note', official_url: 'Official page', official_directory_url: 'Official directory',
  human_work: 'Human work', human_plus_agents: 'Humans + AI agents', automation_scenario: 'AI automation scenario',
  conditions_and_limits: 'Conditions and limits', education_distinction: 'Education distinction', foundations_to_explore: 'Foundations to explore',
  before_paying_questions: 'Before paying, ask', how_to_check: 'How to check', student_takeaway: 'Student takeaway', attribution_note: 'Attribution',
  what_the_arrow_means: 'What the arrow means', employment_effect: 'Employment effect', whole_position_test: 'When a whole position could disappear',
  how_to_use_this: 'How to use this', stronger_future_scenario: 'Stronger future scenario', if_adoption_stalls: 'If adoption stalls',
  agi_asi: 'AGI and ASI', image_note: 'Image note', physical_note: 'Physical work note', student_message: 'Student message',
  workflow_example: 'Workflow example', documented: 'Documented capability', limit: 'Limit', national_example: 'National example',
  time_check: 'Time check', documents_check: 'Documents check', practical_check: 'Practical check', state_open_schools: 'State open schools',
  cuet_example: 'CUET example', equivalence_warning: 'Equivalence warning', learning_note: 'Learning note', location_privacy: 'Location and privacy',
  telangana_notice: 'Telangana notice', local_questions: 'Questions to ask locally', choice_message: 'Your choice', stage_name: 'Stage name',
  important_distinction: 'Important distinction', student_choice: 'Your choice', suggested_route_ids: 'Suggested routes', result_action: 'After the quiz',
  geographic_note: 'Regional note', quiz_note: 'Quiz note', scope_note: 'Scope note', benefits_note: 'Benefits note', job_outcome: 'Job outcome',
  next_action: 'Next action', route_type: 'Route type', reviewed_on: 'Reviewed on', checked_on: 'Checked on', supports: 'Supports', limitation: 'Limitation',
};
export const labelFor = (k) => LABELS[k] || (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, ' ');
const isUrl = (v) => typeof v === 'string' && /^https?:\/\//.test(v);

export const SmartLink = ({ href, children, className = '', testId }) => {
  const internal = INTERNAL[href];
  if (internal) return <Link to={internal} className={className} data-testid={testId}>{children}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>{children}</a>;
};

const CHIP_TONES = { amber: 'bg-amber-100 text-amber-800', teal: 'bg-teal-100 text-teal-800' };
export const Chip = ({ children, tone = 'amber' }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${CHIP_TONES[tone]} break-words max-w-full`}>{children}</span>
);

export const SourceRefs = ({ ids, sources }) => {
  const list = (ids || []).map((id) => sources?.[id]).filter(Boolean);
  if (!list.length) return null;
  return (
    <p className="text-[11px] text-gray-500 mt-2 break-words">Sources: {list.map((s, i) => (
      <span key={s.id}>{i > 0 && '; '}<a href={s.url} target="_blank" rel="noopener noreferrer" className="underline text-teal-700 break-words">{s.publisher}: {s.title}</a></span>
    ))}</p>
  );
};

export const Value = ({ k, v, sources, depth = 0 }) => {
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return null;
  if (isUrl(v)) return <p className="text-sm break-words"><span className="font-semibold text-gray-900">{labelFor(k)}: </span><SmartLink href={v} className="text-teal-700 underline inline-flex items-center gap-1 break-all">{v} <ExternalLink size={12} /></SmartLink></p>;
  if (typeof v === 'boolean') return null;
  if (typeof v !== 'object') {
    if (CHIP_KEYS.has(k)) return <Chip>{String(v)}</Chip>;
    if (k === 'text' || k === 'content' || k === 'intro' || k === 'note' || k === 'message') return <p className="text-sm text-gray-700 leading-relaxed break-words">{String(v)}</p>;
    return <p className="text-sm text-gray-700 leading-relaxed break-words"><span className="font-semibold text-gray-900">{labelFor(k)}: </span>{String(v)}</p>;
  }
  if (Array.isArray(v)) {
    if (typeof v[0] !== 'object') return (
      <div><div className="text-sm font-semibold text-gray-900">{labelFor(k)}</div>
        <ul className="mt-1 space-y-1">{v.map((t, i) => <li key={i} className="text-sm text-gray-700 leading-relaxed flex gap-2"><span className="mt-2 w-1 h-1 rounded-full bg-gray-500 shrink-0" /><span className="break-words min-w-0">{String(t)}</span></li>)}</ul></div>
    );
    // Called as plain functions (not JSX) to keep the Value→Card→Fields cycle out of the JSX graph.
    return <div><div className="text-sm font-semibold text-gray-900 mb-2">{labelFor(k)}</div><div className="grid gap-3 md:grid-cols-2">{v.map((o, i) => <React.Fragment key={o.id || i}>{Card({ obj: o, sources, depth: depth + 1 })}</React.Fragment>)}</div></div>;
  }
  return <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3"><div className="text-sm font-semibold text-gray-900 mb-1">{v.heading || labelFor(k)}</div>{Fields({ obj: v, sources, depth: depth + 1 })}</div>;
};

export const Fields = ({ obj, sources, depth = 0, omit = [] }) => (
  <div className="space-y-2 min-w-0">
    {obj.label && <Chip>{obj.label}</Chip>}
    {Object.entries(obj).filter(([k]) => !HIDE.has(k) && !omit.includes(k) && k !== 'heading').map(([k, v]) => <Value key={k} k={k} v={v} sources={sources} depth={depth} />)}
    <SourceRefs ids={obj.source_ids} sources={sources} />
  </div>
);

export const Card = ({ obj, sources, depth = 0, extra, testId }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm min-w-0" data-testid={testId}>
    {(obj.title || obj.name || obj.question || obj.word || obj.step) && (
      <h4 className="font-bold text-gray-900 text-base leading-snug break-words mb-2">{obj.step ? `${obj.step}. ` : ''}{obj.title || obj.name || obj.question || obj.word}</h4>
    )}
    <Fields obj={obj} sources={sources} depth={depth} omit={['name', 'question', 'word', 'meaning_shown']} />
    {extra}
  </div>
);

/* Section: generic (heading/intro + fields + cards) */
export const GenericSection = ({ data, sources }) => {
  if (Array.isArray(data)) {
    if (typeof data[0] !== 'object') return <ul className="space-y-2">{data.map((t, i) => <li key={i} className="text-sm text-gray-700 leading-relaxed flex gap-2"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" /><span className="break-words min-w-0">{t}</span></li>)}</ul>;
    return <div className="grid gap-3 md:grid-cols-2">{data.map((o, i) => <Card key={o.id || i} obj={o} sources={sources} />)}</div>;
  }
  return <Fields obj={data} sources={sources} />;
};

/* Section: automation_clarity — three stages + notes */
export const AutomationSection = ({ data, sources }) => (
  <div className="space-y-4">
    <Chip tone="amber">{data.label}</Chip>
    <p className="text-sm text-gray-700 leading-relaxed">{data.student_message}</p>
    <div className="grid gap-3 sm:grid-cols-3" data-testid="guide-automation-stages">
      {data.stages.map((s, i) => (
        <div key={s.id} className="rounded-2xl border border-gray-200 bg-white p-4"><div className="text-[10px] font-extrabold uppercase tracking-widest text-teal-700">Stage {i + 1}</div><div className="font-bold text-gray-900 mt-1">{s.title}</div><p className="text-sm text-gray-700 mt-1 leading-relaxed">{s.meaning}</p></div>
      ))}
    </div>
    <Fields obj={data} sources={sources} omit={['stages', 'student_message', 'forecast_end_date', 'job_loss_percentage']} />
  </div>
);

/* Section: routes — cards with linked pathway button */
export const RoutesSection = ({ data, sources, linked }) => (
  <div className="grid gap-3 md:grid-cols-2" data-testid="guide-routes">
    {data.map((r) => {
      const lp = r.linked_pathway_id ? linked?.find((l) => l.id === r.linked_pathway_id) : null;
      return (
        <Card key={r.id} obj={r} sources={sources} testId={`guide-route-${r.id}`} extra={lp && (
          <SmartLink href={lp.url} testId={`guide-route-link-${r.id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-teal-700 hover:text-teal-900">{lp.label} <ArrowRight size={14} /></SmartLink>
        )} />
      );
    })}
  </div>
);

/* Section: field_examples — workflow progression per field */
export const FieldExamplesSection = ({ data, sources, routes }) => (
  <div className="space-y-3" data-testid="guide-field-examples">
    {data.map((f) => (
      <details key={f.id} className="bg-white rounded-2xl border border-gray-200 p-4 group" data-testid={`guide-field-${f.id}`}>
        <summary className="cursor-pointer font-bold text-gray-900 list-none flex items-center justify-between gap-2"><span className="break-words">{f.title}</span><span className="text-gray-400 text-xs group-open:rotate-180 transition-transform">▼</span></summary>
        <div className="mt-3 space-y-3">
          <Chip>{f.label}</Chip>
          <div className="grid gap-2 sm:grid-cols-3">
            {[['human_work', 'Human work'], ['human_plus_agents', 'Humans + AI agents'], ['automation_scenario', 'AI automation scenario']].map(([k, l]) => (
              <div key={k} className="rounded-xl bg-gray-50 border border-gray-100 p-3"><div className="text-[10px] font-extrabold uppercase tracking-widest text-teal-700">{l}</div><p className="text-sm text-gray-700 mt-1 leading-relaxed">{f[k]}</p></div>
            ))}
          </div>
          <Fields obj={f} sources={sources} omit={['human_work', 'human_plus_agents', 'automation_scenario', 'label']} />
          {f.route_ids?.length > 0 && <p className="text-xs text-gray-600"><span className="font-semibold">Related routes: </span>{f.route_ids.map((id) => routes?.find((r) => r.id === id)?.title || id).join(' · ')}</p>}
        </div>
      </details>
    ))}
  </div>
);

/* Section: regional_guidance — pick your state/UT */
export const RegionalSection = ({ data, sources }) => {
  const [sel, setSel] = useState('');
  const entry = data.entries.find((e) => e.id === sel);
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-700 leading-relaxed">{data.coverage}</p>
      <label className="block text-sm font-semibold text-gray-900">Choose your state / Union Territory
        <select value={sel} onChange={(e) => setSel(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm" data-testid="guide-region-select">
          <option value="">— Select —</option>
          {data.entries.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </label>
      {entry && (
        <div className="rounded-2xl border border-teal-200 bg-teal-50/60 p-4" data-testid="guide-region-entry">
          <div className="flex items-center gap-2 font-bold text-gray-900"><MapPin size={16} className="text-teal-700" /> {entry.name} <Chip tone="teal">{entry.jurisdiction_type.replace('_', ' ')}</Chip></div>
          <a href={entry.official_directory_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-teal-700 underline break-all">{entry.official_directory_url} <ExternalLink size={12} /></a>
          <p className="text-xs text-gray-500 mt-1">Checked on {entry.checked_on} · {entry.link_status.replace(/_/g, ' ')}</p>
          <SourceRefs ids={entry.source_ids} sources={sources} />
        </div>
      )}
      <Fields obj={data} sources={sources} omit={['entries', 'coverage']} />
    </div>
  );
};

/* Section: qualification_check (After Inter) — pick what you completed */
export const QualificationSection = ({ data, routes }) => {
  const [sel, setSel] = useState('');
  const q = data.find((x) => x.id === sel);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" data-testid="guide-qualification-options">
        {data.map((x) => <button key={x.id} type="button" onClick={() => setSel(x.id)} data-testid={`guide-qual-${x.id}`} className={`text-left text-sm rounded-xl border-2 px-3 py-2 transition-colors ${sel === x.id ? 'border-teal-500 bg-teal-50 text-teal-900 font-semibold' : 'border-gray-200 bg-white text-gray-800 hover:border-teal-300'}`}>{x.label}</button>)}
      </div>
      {q && (
        <div className="rounded-2xl border border-teal-200 bg-teal-50/60 p-4" data-testid="guide-qualification-result">
          <p className="text-sm text-gray-800 leading-relaxed">{q.guidance}</p>
          <div className="mt-2 text-xs text-gray-700"><span className="font-semibold">Suggested routes to read: </span>{q.suggested_route_ids.map((id) => routes?.find((r) => r.id === id)?.title || id).join(' · ')}</div>
        </div>
      )}
    </div>
  );
};

/* Section: intermediate_groups — links to the existing 5 group quizzes */
export const GroupsSection = ({ data, sources }) => (
  <div className="space-y-3">
    <p className="text-sm text-gray-700 leading-relaxed">{data.note}</p>
    <div className="grid gap-3 md:grid-cols-2">
      {data.cards.map((c) => <Card key={c.id} obj={c} sources={sources} testId={`guide-group-${c.id}`} extra={
        <Link to={`/career-clarity/after-10th/${c.id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-amber-700 hover:text-amber-900" data-testid={`guide-group-quiz-link-${c.id}`}>Open the {c.id} fit quiz <ArrowRight size={14} /></Link>
      } />)}
    </div>
    {data.quiz_note && <p className="text-xs text-gray-600">{data.quiz_note}</p>}
  </div>
);

/* Action buttons (primary_actions / linked_pathways / final_message.actions) */
export const ActionLinks = ({ actions, testIdPrefix }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {actions.map((a) => (
      <SmartLink key={a.id} href={a.url} testId={`${testIdPrefix}-${a.id}`} className="group bg-white border-2 border-teal-200 rounded-2xl px-4 py-3.5 hover:border-teal-400 hover:shadow-md transition-all block">
        <span className="flex items-center justify-between gap-2 font-bold text-gray-900 text-sm">{a.label} <ArrowRight size={16} className="text-teal-600 shrink-0 group-hover:translate-x-0.5 transition-transform" /></span>
        <span className="block text-xs text-gray-600 mt-1 leading-relaxed">{a.description}</span>
      </SmartLink>
    ))}
  </div>
);

export const Notice = ({ children, testId }) => (
  <div className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid={testId}>
    <ShieldAlert size={16} className="text-amber-700 shrink-0 mt-0.5" /><p className="text-sm text-amber-900 leading-relaxed break-words">{children}</p>
  </div>
);

export const useSourceMap = (sources) => useMemo(() => Object.fromEntries((sources || []).map((s) => [s.id, s])), [sources]);
